import * as React from "react"
import { Check, ChevronRight, ChevronsUpDown, Wifi, WifiOff, RotateCcw } from "lucide-react"
import { cn } from "@/utils/classNames"
import { Button } from "@/components/ui/buttonNew"
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { getSites } from "@/services/api/siteService"
import { Site as ApiSite, Device as ApiDevice } from "@/types/api"

// Component props (kept consistent with legacy DeviceSelector)
interface DeviceSelectorProps {
  onDeviceChange: (deviceId: string, timeZoneId?: string) => void
  selectedDeviceId?: string
  placeholder?: string
  className?: string
}

export function DeviceSelector({
  onDeviceChange,
  selectedDeviceId,
  placeholder = "Select a device...",
  className,
}: DeviceSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const [expandedSites, setExpandedSites] = React.useState<Set<string>>(new Set())

  // Data states (aligned with legacy logic)
  const [sites, setSites] = React.useState<ApiSite[]>([])
  const [devices, setDevices] = React.useState<ApiDevice[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)
  const [currentDeviceId, setCurrentDeviceId] = React.useState<string>(selectedDeviceId || "")
  const [hasSingleSite, setHasSingleSite] = React.useState<boolean>(true)

  // Fetch sites and devices (extracted for reuse)
  const refreshDevices = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getSites()
      setSites(response.sites)
      setHasSingleSite(response.sites.length === 1)

      // Flatten all devices
      const allDevices: ApiDevice[] = []
      response.sites.forEach((site) => {
        if (site.device_list && site.device_list.length > 0) {
          allDevices.push(...site.device_list)
        }
      })

      if (allDevices.length > 0) {
        const sortedDevices = [...allDevices].sort((a, b) => {
          const aName = a.tag || a.deviceId
          const bName = b.tag || b.deviceId
          return aName.localeCompare(bName)
        })
        setDevices(sortedDevices)

        // If no external selection, default to the first device
        if (!selectedDeviceId && sortedDevices.length > 0 && !currentDeviceId) {
          const deviceToSelect = sortedDevices[0].deviceId
          setCurrentDeviceId(deviceToSelect)
          onDeviceChange(deviceToSelect, "")
        }
      }
    } catch (err) {
      console.error("Error fetching devices:", err)
      setError("Failed to fetch devices")
    } finally {
      setLoading(false)
    }
  }, [selectedDeviceId, currentDeviceId, onDeviceChange])

  React.useEffect(() => {
    refreshDevices()
  }, [refreshDevices])

  // Sync with external selected device ID changes
  React.useEffect(() => {
    if (selectedDeviceId && selectedDeviceId !== currentDeviceId) {
      setCurrentDeviceId(selectedDeviceId)
    }
  }, [selectedDeviceId, currentDeviceId])

  // Handle search (local filtering only)
  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchQuery(query)
      const q = query.trim().toLowerCase()
      if (!q) {
        // 清空搜索时不改变用户已有的展开状态
        return
      }

      // 搜索命中多个站点：收集所有命中的站点并全部展开，
      // 同时把当前选中设备定位到第一个匹配项。
      const matchedSiteIds = new Set<string>()
      let firstMatchDeviceId: string | null = null

      for (const site of sites) {
        const idx = site.device_list.findIndex((d) => (d.address || "").toLowerCase().includes(q))
        if (idx !== -1) {
          matchedSiteIds.add(site.objectId)
          if (!firstMatchDeviceId) {
            firstMatchDeviceId = site.device_list[idx].deviceId
          }
        }
      }

      if (matchedSiteIds.size > 0) {
        setExpandedSites(matchedSiteIds)
        if (firstMatchDeviceId) setCurrentDeviceId(firstMatchDeviceId)
      }
    },
    [sites],
  )

  // WiFi status icon component (matches existing UI)
  const WifiStatusIcon = ({ isOnline }: { isOnline: boolean }) => {
    return isOnline ? (
      <Wifi className="h-4 w-4 text-green-500" />
    ) : (
      <WifiOff className="h-4 w-4 text-muted-foreground" />
    )
  }

  // Get selected device by current ID
  const getSelectedDevice = React.useCallback(() => {
    if (!currentDeviceId) return null

    const inFlat = devices.find((d) => d.deviceId === currentDeviceId)
    if (inFlat) return inFlat

    for (const site of sites) {
      const device = site.device_list.find((d) => d.deviceId === currentDeviceId)
      if (device) return device
    }
    return null
  }, [currentDeviceId, devices, sites])

  const selectedDevice = getSelectedDevice()

  // Render a device item (UI unchanged, just mapping fields)
  const renderDeviceItem = (device: ApiDevice) => (
    <CommandItem
      key={device.objectId}
      value={device.deviceId}
      className="transition-colors cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-foreground"
      onSelect={(currentValue) => {
        const newValue = currentValue === currentDeviceId ? "" : currentValue
        setCurrentDeviceId(newValue)
        onDeviceChange(newValue, "")
        setOpen(false)
      }}
    >
      <div className="flex items-center gap-2 flex-1">
        <WifiStatusIcon isOnline={Boolean(device.online)} />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="font-medium truncate">{device.tag || device.deviceId}</span>
          <span className="text-xs text-muted-foreground truncate">{device.address}</span>
        </div>
      </div>
      <Check className={cn(
        "ml-2 h-5 w-5 flex-shrink-0 text-green-600",
        currentDeviceId === device.deviceId ? "opacity-100" : "opacity-0"
      )} />
    </CommandItem>
  )

  const toggleSite = (siteId: string) => {
    setExpandedSites((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(siteId)) {
        newSet.delete(siteId)
      } else {
        newSet.add(siteId)
      }
      return newSet
    })
  }

  // Filter devices by search keyword
  const filterDevices = (list: ApiDevice[]) => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return list
    // Frontend filtering by address only (case-insensitive)
    return list.filter((d) => (d.address || "").toLowerCase().includes(q))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            // 去掉蓝色边框与聚焦时的外圈
            "border-transparent focus-visible:ring-0 focus:ring-0 focus-visible:outline-none outline-none focus-visible:ring-offset-0 ring-offset-0",
            className,
          )}
        >
          {selectedDevice ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <WifiStatusIcon isOnline={Boolean((selectedDevice as ApiDevice).online)} />
              <span className="truncate">{selectedDevice.tag || selectedDevice.deviceId}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0 bg-white dark:bg-gray-800" align="start">
        <Command shouldFilter={false} className="bg-white dark:bg-gray-800 focus:outline-none focus:ring-0 ring-0">
          <div className="flex items-center px-2 py-2 gap-2">
            <div className="flex-1 min-w-0">
              <CommandInput
                placeholder="Search devices..."
                value={searchQuery}
                onValueChange={handleSearch}
                autoFocus
                className="w-full border-0 border-transparent focus:border-transparent focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none ring-0"
              />
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => refreshDevices()}
              disabled={loading}
            >
              {loading ? (
                <RotateCcw className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
            </Button>
          </div>
          <CommandList>

            {hasSingleSite ? (
              // Single site: flat device list
              <CommandGroup>{filterDevices(devices).map((device) => renderDeviceItem(device))}</CommandGroup>
            ) : (
              // Multiple sites: grouped by site
              <>
                {sites.map((site) => (
                  <CommandGroup key={site.objectId}>
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold cursor-pointer hover:bg-accent rounded-sm"
                      onClick={() => toggleSite(site.objectId)}
                    >
                      <ChevronRight
                        className={cn("h-4 w-4 transition-transform", expandedSites.has(site.objectId) && "rotate-90")}
                      />
                      <span>{site.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">({site.device_list.length})</span>
                    </div>
                    {expandedSites.has(site.objectId) && (
                      <div className="ml-6">{filterDevices(site.device_list).map((device) => renderDeviceItem(device))}</div>
                    )}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
      {error && <div className="text-xs text-red-500 px-2 py-1">{error}</div>}
    </Popover>
  )
}
